import * as React from "react";
import { styled } from "@mui/material/styles";
import autobind from "autobind-decorator";
import Vector2 from "../editor/Vector2";
import Editor from "../editor/Editor";
import MouseEventArgs, { MouseButton } from "../editor/MouseEventArgs";
import { inject, observer } from "mobx-react";
import NodeLayer from "../editor/NodeLayer";
// import { TouchEvent } from '../editor/MouseEventArgs';

const StyledCanvas = styled("canvas")({
  position: "absolute",
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
  letterSpacing: "normal",
});

type DrawAreaViewProps = {
  editor?: Editor;
  className?: string;
  style?: React.CSSProperties;
};

@inject("editor")
@observer
export default class DrawAreaView extends React.Component<DrawAreaViewProps> {
  private _context: CanvasRenderingContext2D;
  private _canvas: HTMLCanvasElement;
  private _disposed = false;
  private y1: number = 0;
  private y2: number = 0;
  private x1: number = 0;
  private x2: number = 0;

  @autobind
  canvasRef(ref: HTMLCanvasElement) {
    this._canvas = ref;
  }

  @autobind
  handleKey(ev: KeyboardEvent) {
    if ((ev.target as any).type == "text") {
      return;
    }

    if (ev.keyCode == 8) {
      this.handleDelete();
    }
  }

  handleDelete() {
    if (this.props.editor.currentTool == null) {
      return;
    }

    this.props.editor.currentTool.handleDelete();
  }

  clearScreen() {
    this._context.fillStyle = "rgb(230, 230, 230)";
    this._context.fillRect(0, 0, this._canvas.width, this._canvas.height);
  }

  @autobind
  update() {
    if (this._disposed) {
      return;
    }

    var canvasSize = new Vector2(
      this._canvas.clientWidth * window.devicePixelRatio,
      this._canvas.clientHeight * window.devicePixelRatio
    );

    if (
      this._canvas.width != canvasSize.x ||
      this._canvas.height != canvasSize.y
    ) {
      this._canvas.width = canvasSize.x;
      this._canvas.height = canvasSize.y;
    }

    this.clearScreen();

    this.props.editor.draw(this._context);
    this.props.editor.setCanvaSize(this._canvas);

    requestAnimationFrame(this.update);
  }

  componentDidMount() {
    this._context = this._canvas.getContext("2d");
    window.addEventListener("keyup", this.handleKey);
    this.update();
  }

  componentWillUnmount() {
    this._disposed = true;
    window.removeEventListener("keyup", this.handleKey);
  }

  @autobind
  handleMouseDown(ev: React.MouseEvent | React.TouchEvent) {
    // handleMouseDown(ev: React.MouseEvent | React.TouchEvent)
    // console.log('ev.touh', ev.nativeEvent);
    var screenPos: any;
    if (ev.nativeEvent && ev.nativeEvent?.changedTouches !== undefined) {
      screenPos = new Vector2(
        Math.round(ev.nativeEvent.changedTouches[0].clientX) *
          window.devicePixelRatio,
        (Math.round(ev.nativeEvent.changedTouches[0].clientY) - 48) *
          window.devicePixelRatio
      );
    } else {
      screenPos = new Vector2(
        ev.nativeEvent["layerX"] * window.devicePixelRatio,
        ev.nativeEvent["layerY"] * window.devicePixelRatio
      );
    }
    var worldPos =
      this.props.editor.camera.transformPointScreenToWorld(screenPos);

    this.props.editor.rootLayerGroup.layers.forEach((layer) => {
      if (layer instanceof NodeLayer) {
        layer.handleArrowClick(worldPos);
      }
    });

    this.props.editor.currentTool.handleMouseDown(
      new MouseEventArgs(screenPos, worldPos, 0)
    );
  }

  @autobind
  handleMouseUp(ev: React.MouseEvent | React.TouchEvent) {
    var screenPos: any;
    // if (ev.nativeEvent instanceof TouchEvent) {
    if (ev.nativeEvent && ev.nativeEvent?.changedTouches !== undefined) {
      screenPos = new Vector2(
        Math.round(ev.nativeEvent.changedTouches[0].clientX) *
          window.devicePixelRatio,
        (Math.round(ev.nativeEvent.changedTouches[0].clientY) - 48) *
          window.devicePixelRatio
      );
    } else {
      screenPos = new Vector2(
        ev.nativeEvent["layerX"] * window.devicePixelRatio,
        ev.nativeEvent["layerY"] * window.devicePixelRatio
      );
    }
    // screenPos = new Vector2(
    //   ev.nativeEvent["layerX"] * window.devicePixelRatio,
    //   ev.nativeEvent["layerY"] * window.devicePixelRatio
    // );
    var worldPos =
      this.props.editor.camera.transformPointScreenToWorld(screenPos);

    this.props.editor.currentTool.handleMouseUp(
      new MouseEventArgs(screenPos, worldPos, 0)
    );
  }

  @autobind
  handleMouseMove(ev: React.MouseEvent | React.TouchEvent) {
    // handleMouseMove(ev: React.MouseEvent) {
    var screenPos: any;
    if (ev.nativeEvent && ev.nativeEvent?.changedTouches !== undefined) {
      if (ev.nativeEvent?.targetTouches.length === 2) {
        let currentPointX1 = 0;
        let currentPointY1 = 0;
        let currentPointX2 = 0;
        let currentPointY2 = 0;
        if (
          ev.nativeEvent?.targetTouches[1]?.clientY >
          ev.nativeEvent?.targetTouches[0]?.clientY
        ) {
          currentPointX1 = ev.nativeEvent?.targetTouches[0]?.clientX;
          currentPointY1 = ev.nativeEvent?.targetTouches[0]?.clientY;
          currentPointX2 = ev.nativeEvent?.targetTouches[1]?.clientX;
          currentPointY2 = ev.nativeEvent?.targetTouches[1]?.clientY;
        } else {
          currentPointX1 = ev.nativeEvent?.targetTouches[1]?.clientX;
          currentPointY1 = ev.nativeEvent?.targetTouches[1]?.clientY;
          currentPointX2 = ev.nativeEvent?.targetTouches[0]?.clientX;
          currentPointY2 = ev.nativeEvent?.targetTouches[0]?.clientY;
        }
        const diffX = (currentPointX1 + currentPointX2) / 2;
        const diffY = (currentPointY1 + currentPointY2) / 2;
        screenPos = new Vector2(
          Math.round(diffX) * window.devicePixelRatio,
          Math.round(diffY) * window.devicePixelRatio
        );
        var worldPos =
          this.props.editor.camera.transformPointScreenToWorld(screenPos);
        if (this.y1 - currentPointY1 > 0 && this.y2 - currentPointY2 < 0) {
          const args = new MouseEventArgs(
            screenPos,
            worldPos,
            1,
            -68 * window.devicePixelRatio
          );
          this.props.editor.currentTool.handleMouseWheel(args);
        } else if (
          this.y1 - currentPointY1 < 0 &&
          this.y2 - currentPointY2 > 0
        ) {
          const args = new MouseEventArgs(
            screenPos,
            worldPos,
            1,
            68 * window.devicePixelRatio
          );
          this.props.editor.currentTool.handleMouseWheel(args);
        }

        this.y1 = currentPointY1;
        this.x1 = currentPointX1;
        this.x2 = currentPointX2;
        this.y2 = currentPointY2;
      } else {
        screenPos = new Vector2(
          Math.round(ev.nativeEvent.changedTouches[0].clientX) *
            window.devicePixelRatio,
          (Math.round(ev.nativeEvent.changedTouches[0].clientY) - 48) *
            window.devicePixelRatio
        );
        var worldPos =
          this.props.editor.camera.transformPointScreenToWorld(screenPos);

        this.props.editor.currentTool.handleMouseMove(
          new MouseEventArgs(screenPos, worldPos, 0)
        );
      }
      // console.log('y1', this.y1);
      // console.log('currentPoint', currentPoint);
      // console.log('value', this.y1 - currentPoint);
      // if(ev.nativeEvent?.changedTouches?.lengh === 2 && ev.nativeEvent.targetTouches.length === 2){

      // }
      // console.log('targetTouches.length', ev.nativeEvent.targetTouches.length);
      // console.log(
      //   'changedTouches.length',
      //   ev.nativeEvent?.changedTouches.length
      // );
      // console.log(
      //   'ev.nativeEvent?.changedTouches[0]?.clientY',
      //   ev.nativeEvent?.changedTouches[0]?.clientY
      // );
      // console.log(
      //   'ev.nativeEvent?.changedTouches[1]',
      //   ev.nativeEvent?.changedTouches[1]
      // );
      // console.log(
      //   'ev.nativeEvent?.targetTouches[0]?.clientY',
      //   ev.nativeEvent?.targetTouches[0]?.clientY
      // );
      // console.log(
      //   'ev.nativeEvent?.changedTouches[0]',
      //   ev.nativeEvent?.targetTouches[1]
      // );
      // screenPos = new Vector2(
      //   Math.round(ev.nativeEvent.changedTouches[0].clientX) *
      //     window.devicePixelRatio,
      //   (Math.round(ev.nativeEvent.changedTouches[0].clientY) - 48) *
      //     window.devicePixelRatio
      // );
    }
    if (ev.nativeEvent instanceof MouseEvent) {
      screenPos = new Vector2(
        ev.nativeEvent["layerX"] * window.devicePixelRatio,
        ev.nativeEvent["layerY"] * window.devicePixelRatio
      );
      var worldPos =
        this.props.editor.camera.transformPointScreenToWorld(screenPos);

      this.props.editor.currentTool.handleMouseMove(
        new MouseEventArgs(screenPos, worldPos, 0)
      );
    }
  }

  @autobind
  handleMouseWheel(ev: React.WheelEvent) {
    ev.preventDefault();
    var screenPos = new Vector2(
      ev.nativeEvent["layerX"] * window.devicePixelRatio,
      ev.nativeEvent["layerY"] * window.devicePixelRatio
    );
    var worldPos =
      this.props.editor.camera.transformPointScreenToWorld(screenPos);

    var args = new MouseEventArgs(
      screenPos,
      worldPos,
      MouseButton.Middle,
      ev.deltaY * window.devicePixelRatio
    );
    this.props.editor.currentTool.handleMouseWheel(args);
  }

  render() {
    return (
      <StyledCanvas
        className={this.props.className}
        style={{ cursor: this.props.editor.cursor, ...this.props.style }}
        width="1280"
        height="720"
        ref={this.canvasRef}
        onMouseDown={this.handleMouseDown}
        onMouseUp={this.handleMouseUp}
        onMouseLeave={this.handleMouseUp}
        onMouseMove={this.handleMouseMove}
        onWheel={this.handleMouseWheel}
        onTouchStart={this.handleMouseDown}
        onTouchEnd={this.handleMouseUp}
        onTouchMove={this.handleMouseMove}
        onTouchCancel={this.handleMouseUp}
      />
    );
  }
}
